import React, { useCallback, useEffect, useState } from "react";
import {
  getPlanningCell, assignPlanning, updateTask, updateOtherTask,
} from "../../api";
import { MONTH_LABEL, UNSCHEDULED } from "./cellFormat";

// One cell's worth of tasks, fetched on demand. The grid endpoint carries
// aggregates only; this is the single place task rows cross the wire, and it
// asks for exactly one cell at a time.

// Height of the fixed TopBar (components/TopBar.jsx renders a 64px spacer of
// the same size). Kept as a named constant so the relationship is explicit
// rather than a bare 64 someone later "tidies away".
const TOP_BAR_HEIGHT = 64;

const rowKeyOf = (row) => `${row.kind}-${row.id}`;

// The sentinel the month picker uses for "clear the override". It has to be
// distinct from "" -- "" is the untouched placeholder, and treating those
// two as one would clear every selected row's planned_period the moment a
// manager pressed the button without choosing anything.
const DERIVED = "derived";

export default function PlanningCellDrawer({
  rowKey, month, employees, categories = [], plannedOnly = false,
  monthOptions = [], onClose, onAssigned,
}) {
  const [rows, setRows] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [assignee, setAssignee] = useState("");
  const [monthChoice, setMonthChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // The Unscheduled cell is keyed by a sentinel, not a date, so it must not
  // be sliced to YYYY-MM -- that would send "unsched" and the server would
  // reject it as a malformed month.
  const isUnscheduled = month === UNSCHEDULED;
  const monthParam = isUnscheduled ? UNSCHEDULED : month.slice(0, 7);

  // The drawer must ask the server for the SAME scope the cell it was opened
  // from was counted under, or a cell reading "7" opens onto a different set
  // of rows. `categories` arrives as an array, whose identity changes on
  // every parent render, so the effect depends on the joined string instead
  // -- otherwise the fetch refires forever.
  const catParam = categories.join(",");

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setLoadFailed(false);
    setError("");
    const params = { employee: rowKey, month: monthParam };
    if (catParam) params.categories = catParam;
    if (plannedOnly) params.planned_only = 1;
    getPlanningCell(params)
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) {
          // Keep the failed state distinct from "loaded and empty" -- rows
          // stays null so the empty-state text never shows alongside the
          // error, which would tell the manager two contradictory things.
          setLoadFailed(true);
          setError("Could not load this cell.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rowKey, monthParam, catParam, plannedOnly]);

  const toggle = useCallback((row) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = rowKeyOf(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleAssign = async () => {
    setBusy(true);
    setError("");
    try {
      const chosen = (rows || []).filter((r) => selected.has(rowKeyOf(r)));
      await assignPlanning({
        task_ids: chosen.filter((r) => r.kind === "task").map((r) => r.id),
        other_task_ids: chosen
          .filter((r) => r.kind === "other-task")
          .map((r) => r.id),
        employee_id: assignee === "" ? null : parseInt(assignee, 10),
      });
      onAssigned();
    } catch {
      setError("Could not assign those tasks.");
    } finally {
      setBusy(false);
    }
  };

  // Moving work to a different month writes `planned_period`, which
  // month_expression() gives top priority over the due date and year end.
  // "Derived month" sends null, which returns the task to whatever its dates
  // imply. This is the minimal control; drag-to-month is not built.
  const handleMove = async () => {
    setBusy(true);
    setError("");
    try {
      const chosen = (rows || []).filter((r) => selected.has(rowKeyOf(r)));
      const value = monthChoice === DERIVED ? null : monthChoice;
      await Promise.all(
        chosen.map((r) =>
          r.kind === "task"
            ? updateTask(r.id, { planned_period: value })
            : updateOtherTask(r.id, { planned_period: value })
        )
      );
      onAssigned();
    } catch {
      setError("Could not move those tasks.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        // Sits BELOW the fixed top bar, not under it. TopBar is 64px tall and
        // renders at zIndex 1000 (components/TopBar.jsx); this drawer used to
        // be top:0 / zIndex:50, so its heading and first rows were painted
        // over by the bar and were unreachable. Offsetting by the bar's height
        // and clearing its stacking order fixes both halves -- the offset
        // alone would still lose anything that scrolled up under it.
        position: "fixed", top: TOP_BAR_HEIGHT, right: 0, bottom: 0, width: 460,
        background: "#fff", borderLeft: "1px solid #E5E7EB",
        boxShadow: "-8px 0 24px rgba(0,0,0,0.06)", padding: 20,
        overflowY: "auto", zIndex: 1100,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>
          {rowKey === "unassigned"
            ? "Unassigned"
            : (employees.find((e) => String(e.id) === rowKey) || {}).name ||
              "Employee"}
        </h3>
        <button onClick={onClose} aria-label="Close">×</button>
      </div>

      {loadFailed ? null : rows === null ? (
        <p style={{ color: "#6B7280" }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: "#6B7280" }}>
          {isUnscheduled
            ? "Nothing unscheduled — every task has a month."
            : "Nothing planned in this month."}
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, marginTop: 16 }}>
          {rows.map((row) => (
            <li
              key={rowKeyOf(row)}
              style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                padding: "8px 0", borderBottom: "1px solid #F3F4F6",
              }}
            >
              <input
                type="checkbox"
                data-testid={`select-${rowKeyOf(row)}`}
                checked={selected.has(rowKeyOf(row))}
                onChange={() => toggle(row)}
              />
              <div>
                <div>{row.name}</div>
                <div style={{ color: "#6B7280", fontSize: 12 }}>
                  {row.category}
                  {row.due_date ? ` · due ${row.due_date}` : ""}
                  {row.status ? ` · ${row.status}` : ""}
                  {row.is_planned ? "" : " · in progress"}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <select
          data-testid="assignee-select"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          style={{ flex: 1 }}
        >
          <option value="">Unassign</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
        <button
          onClick={handleAssign}
          disabled={busy || selected.size === 0}
        >
          Assign
        </button>
      </div>

      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
        <select
          data-testid="month-select"
          value={monthChoice}
          onChange={(e) => setMonthChoice(e.target.value)}
          style={{ flex: 1 }}
        >
          <option value="">Move to month…</option>
          {monthOptions.map((m) => (
            <option key={m} value={m}>{MONTH_LABEL(m)}</option>
          ))}
          <option value={DERIVED}>Derived month</option>
        </select>
        <button
          onClick={handleMove}
          disabled={busy || selected.size === 0 || monthChoice === ""}
        >
          Set month
        </button>
      </div>

      {error ? (
        <p style={{ color: "#B91C1C", fontSize: 13 }}>{error}</p>
      ) : null}
    </div>
  );
}
